export const movingAverageFilter = (windowSize) => (v, i, data) => {
  // ウィンドウの半径を計算
  const radius = Math.floor(windowSize / 2)
  // ウィンドウの開始と終了のインデックスを計算
  const start = Math.max(0, i - radius)
  const end = Math.min(data.length, i + radius + 1)
  // ウィンドウ内の要素の合計を計算
  const sum = data.slice(start, end).reduce((acc, curr) => acc + curr, 0)
  // 平均値を計算して返す
  return sum / (end - start)
}

export const medianFilter =
  ({ windowSize }) =>
  (v, i, data) => {
    // ウィンドウの半径を計算
    const radius = Math.floor(windowSize / 2)
    // ウィンドウ内のデータを取得
    const window = data.slice(Math.max(0, i - radius), Math.min(data.length, i + radius + 1))
    // ウィンドウ内のデータをソート
    const sorted = [...window].sort((a, b) => a - b)
    // 中央値を返す
    return sorted[Math.floor(sorted.length / 2)]
  }

export const deviationFilter = (windowSize = 5, deviationThreshold = 3) => {
  return (data) => {
    const excludeSelf = true
    const keepWindowSize = true
    const radius = Math.floor(windowSize / 2)
    const getWindow = (data, i) => {
      const minEnd = keepWindowSize ? data.length : 0
      const maxStart = keepWindowSize ? data.length - 1 - windowSize : data.length - 1
      const start = Math.max(0, Math.min(i - radius, maxStart))
      const end = Math.min(data.length, Math.max(i + radius + 1, minEnd))
      const window = excludeSelf
        ? [...data.slice(start, i), ...data.slice(i + 1, end)] // 自分自身を除いたウィンドウを取得
        : data.slice(start, end) // 自分を含むウィンドウを取得
      return window
    }
    const filteredIndex = []
    const filter = (v, i, data) => {
      const window = getWindow(data, i)
      const stats = getDataStatistics(window)
      const { mean, std } = stats
      // 自分の値と平均との差を標準偏差で割って、標準化偏差を計算
      const standardizedDeviation = Math.abs(v - mean) / std
      // 許容範囲内なら元の値を返す
      if (standardizedDeviation <= deviationThreshold) {
        return v
      }
      filteredIndex.push(i)
      // 許容範囲外ならnullを返す（または他の適切な値）
      return null
    }
    const filteredData = data.map(filter)
    return {
      filteredData,
      filteredIndex,
    }
  }
}

export const medianInterpolationFilter = (windowSize = 5) => {
  let rawValues = [] // 実測値データを保持

  return {
    addValue: (newRawValue) => {
      // 新しい実測値を追加
      rawValues.push(newRawValue)

      // ウィンドウサイズを超えた場合は古いデータを削除
      if (rawValues.length > windowSize * 10) {
        // バッファサイズは適度に大きく
        rawValues = rawValues.slice(-windowSize * 5)
      }

      // 現在の値を含む前windowSize個の実測値で中央値を計算
      const start = Math.max(0, rawValues.length - windowSize)
      const window = rawValues.slice(start)

      if (window.length === 0) return newRawValue

      const sorted = [...window].sort((a, b) => a - b)
      return sorted[Math.floor(sorted.length / 2)]
    },

    // 既存のバッチ処理用（後方互換性のため）
    process: (data) => {
      return data.map((value, index) => {
        const start = Math.max(0, index - windowSize + 1)
        const end = index + 1
        const window = data.slice(start, end)

        if (window.length === 0) return value

        const sorted = [...window].sort((a, b) => a - b)
        return sorted[Math.floor(sorted.length / 2)]
      })
    },

    reset: () => {
      rawValues = []
    },
  }
}

// 防水温度センサーDS18B20の温度測定でハードウェア限界外の値を除外するフィルタ処理
// 異常値スパイクのワークアラウンド（スパイク検出・中央値フィルタ）はメインスレッドのブロック起因の根本原因対策後に削除した。
// 万が一不安定なら復活させる前提でハードウェア限界チェックのみ残してある。
export const sanitizeWaterTemperatureFilter = () => {
  // DS18B20のデータシート上の温度範囲 -55～+125degC
  // 理論的な最小温度：0xF800→ −2048/16 = −128.0 degC
  // 理論的な最大温度：0x07FF→ 2047/16 = +127.9375 degC
  const minTemp = -78.5 // 下限は実測できないが十分余裕をもった値に設定することで-55degCから多少下方向にオーバーシュートした温度範囲もカバーできる。下方向はどこでセンサーが飽和するかわからないので、センサー定格外ではあるけど低温の実験で使われそうなドライアイスの-78.5degCまでカバーしておけば安心かな
  const maxTemp = 128.0 // 実測での飽和値は127.9degCだった。理論値と一致。余裕をみて128degCに設定。異常値として131.9degCが応答されたことがあるので、そういった異常値は判別できる

  let lastFiltered = null

  return {
    addValue: (newRawValue) => {
      // センサの最小最大温度範囲外ならそれは異常値として扱い、前回の採用値をそのまま返す
      if (newRawValue == null || newRawValue < minTemp || newRawValue > maxTemp) {
        return lastFiltered
      }
      lastFiltered = newRawValue
      return newRawValue
    },

    // 既存のバッチ処理用（後方互換性のため）
    process: (data) => data,

    reset: () => {
      lastFiltered = null
    },
  }
}

/**
 * 描画用にデータを間引く。全データはメモリに保持したまま、Chart.jsに渡す点数を減らす。
 *
 * 設計上の工夫:
 * - 最新のrecentCount点はそのまま含める（リアルタイム性確保）
 * - 過去データは整数bucketSizeで分割し、各バケットの「最小値」と「最大値」を採用
 *   - 整数化することで bucketSize が変わらない限りサンプル位置が動かず、外れ値のチカチカが起きない
 *   - 最大・最小の両方を採用することで、スパイク等の重要な値を取りこぼさない
 *   - bucketSize が変わるのは historical.length が numBuckets の倍数を超えた時だけ（数百件単位）
 */
export const downsampleForDisplay = (data, maxPoints = 500) => {
  const len = data.length
  if (len <= maxPoints) return data

  // 最新数点はリアルタイム表示のためそのまま含める
  const recentCount = Math.min(10, Math.floor(maxPoints * 0.05) || 1)
  // 各バケットから min/max の2点を出力するのでバケット数は半分
  const numBuckets = Math.max(1, Math.floor((maxPoints - recentCount) / 2))

  const historical = data.slice(0, len - recentCount)
  const recent = data.slice(len - recentCount)

  // 整数bucketSize にして、データ追加してもサンプル位置が動かないようにする
  const bucketSize = Math.max(1, Math.ceil(historical.length / numBuckets))
  const sampled = []
  for (let bucketStart = 0; bucketStart < historical.length; bucketStart += bucketSize) {
    const bucketEnd = Math.min(bucketStart + bucketSize, historical.length)
    let minIdx = bucketStart
    let maxIdx = bucketStart
    for (let i = bucketStart + 1; i < bucketEnd; i++) {
      if (historical[i].y < historical[minIdx].y) minIdx = i
      if (historical[i].y > historical[maxIdx].y) maxIdx = i
    }
    // 時系列順を保つため、minIdx と maxIdx を index 順に push
    if (minIdx === maxIdx) {
      sampled.push(historical[minIdx])
    } else {
      const first = Math.min(minIdx, maxIdx)
      const second = Math.max(minIdx, maxIdx)
      sampled.push(historical[first])
      sampled.push(historical[second])
    }
  }

  return sampled.concat(recent)
}

const getDataStatistics = (data) => {
  const count = data.length
  const sum = data.reduce((acc, val) => acc + val, 0)
  const avg = sum / count
  const variance = data.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) / count
  const sortedData = [...data].sort((a, b) => a - b)
  const median = count % 2 === 0 ? (sortedData[count / 2 - 1] + sortedData[count / 2]) / 2 : sortedData[Math.floor(count / 2)]
  return {
    count,
    sum,
    avg,
    mean: avg,
    variance,
    std: Math.sqrt(variance),
    median,
    min: sortedData[0],
    max: sortedData[count - 1],
  }
}
